import React, { useState, useEffect } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import app from "../firebase";

const UserIssuesPage = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // New state for search input
  const db = getFirestore(app);

  useEffect(() => {
    const fetchIssues = async () => {
      try {
        setLoading(true);
        const querySnapshot = await getDocs(collection(db, "issues"));
        const fetchedIssues = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setIssues(fetchedIssues);
      } catch (error) {
        console.error("Error fetching issues: ", error);
        setAlertMessage("Failed to fetch issues. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchIssues();
  }, [db]);

  const handleStatusChange = async (id, status) => {
    try {
      const issueDoc = doc(db, "issues", id);
      await updateDoc(issueDoc, { status });
      setIssues((prevIssues) =>
        prevIssues.map((issue) =>
          issue.id === id ? { ...issue, status } : issue
        )
      );
      setAlertMessage(`Issue marked as ${status} successfully!`);
    } catch (error) {
      console.error("Error updating status: ", error);
      setAlertMessage("Failed to update status. Please try again.");
    }
  };

  const handleDelete = async (id) => {
    try {
      const issueDoc = doc(db, "issues", id);
      await deleteDoc(issueDoc);
      setIssues((prevIssues) => prevIssues.filter((issue) => issue.id !== id));
      setAlertMessage("Issue deleted successfully!");
    } catch (error) {
      console.error("Error deleting issue: ", error);
      setAlertMessage("Failed to delete issue. Please try again.");
    }
  };

  // Filter issues based on search query
  const filteredIssues = issues.filter((issue) =>
    issue.status.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <p className="text-lg text-gray-600">Loading issues...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg mt-8">
      <h1 className="text-2xl font-bold text-gray-800 mb-6 text-center">
        User Issues
      </h1>
      {/* Search Input */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by status (e.g., pending, Completed, Rejected)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600"
        />
      </div>
      {alertMessage && (
        <div
          className={`p-4 mb-6 rounded-md text-sm ${
            alertMessage.includes("successfully")
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {alertMessage}
        </div>
      )}
      {filteredIssues.length === 0 ? (
        <p className="text-gray-600 text-center">No issues found.</p>
      ) : (
        <div className="space-y-4">
          {filteredIssues.map((issue) => (
            <div
              key={issue.id}
              className="border border-gray-200 rounded-md p-4 bg-gray-50 hover:bg-gray-100 transition duration-200"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    <strong>Name:</strong> {issue.name || "N/A"}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Email:</strong> {issue.email || "N/A"}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Report:</strong> {issue.report || "N/A"}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Status:</strong> {issue.status || "N/A"}
                  </p>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => handleStatusChange(issue.id, "Completed")}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-200"
                  >
                    Mark as Completed
                  </button>
                  <button
                    onClick={() => handleStatusChange(issue.id, "Rejected")}
                    className="bg-yellow-500 text-white px-4 py-2 rounded-md hover:bg-yellow-600 transition duration-200"
                  >
                    Mark as Rejected
                  </button>
                  <button
                    onClick={() => handleDelete(issue.id)}
                    className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition duration-200"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserIssuesPage;
